import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchDashboard } from '../../../../features/dashboard/dashboardThunks';
import { setFilters } from '../../../../features/dashboard/dashboardSlice';
import { DASHBOARD_MIN_REFETCH_MS } from '../../../../features/dashboard/dashboardConstants';
import { setFilters as setDeliveryFilters } from '../../../../features/deliveries/deliveriesSlice';
import { setFilters as setAttentionFilters } from '../../../../features/attention/attentionSlice';
import { FAMILY_COLOR } from '../../../../utils/chartColors';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { StaleDataNotice } from '../../../../components/ui/StaleDataNotice';

import { DashboardFilters } from './components/DashboardFilters';
import { DashboardKpis } from './components/DashboardKpis';
import { DashboardCharts } from './components/DashboardCharts';
import { RecentActivityCard } from './components/RecentActivityCard';
import { NeedsAttentionCard } from './components/NeedsAttentionCard';
import { RankingCard } from './components/RankingCard';

/**
 * The seller's control tower.
 *
 * `api/dashboard/metrics/` scopes itself to the seller's company from the
 * session, so there is no company to pick and no ranking by company — it would
 * be a single bar.
 *
 * The backend publishes the `stale` event to the seller's admin only
 * (`deliveries/signals.py`), so this screen does not refresh itself on a status
 * change yet. The refetch on reconnect and the reload button still work.
 */
export const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    kpis, previous, range, statusCount, activity, daily, communeCount, needsAttention,
    filters, isLoading, isStale, lastFetchedAt, error,
  } = useSelector((state) => state.dashboard);
  const { isConnected } = useSelector((state) => state.notifications);

  /**
   * The slice is shared with the admin's dashboard, which does have a company
   * picker, and now it is persisted. A company left there would narrow this
   * screen with nothing on it to show or undo it — the same reason the seller's
   * deliveries table neutralises the filters it does not offer.
   */
  const hasAdminFilter = filters.company !== 'all';

  useEffect(() => {
    // Correcting and fetching in one effect: split in two, the mount would fire
    // one request against the stale filter and a second one without it.
    if (hasAdminFilter) {
      dispatch(setFilters({ company: 'all' }));
      return;
    }

    dispatch(fetchDashboard());
  }, [dispatch, filters, hasAdminFilter]);

  // Reconnecting is the moment we know we may have missed notices: the backend
  // publishes them and forgets.
  const wasConnected = useRef(isConnected);
  useEffect(() => {
    if (isConnected && !wasConnected.current) dispatch(fetchDashboard());
    wasConnected.current = isConnected;
  }, [isConnected, dispatch]);

  useEffect(() => {
    if (!isStale || isLoading) return;

    const elapsed = Date.now() - (lastFetchedAt || 0);
    if (elapsed >= DASHBOARD_MIN_REFETCH_MS) {
      dispatch(fetchDashboard());
      return;
    }

    const timer = setTimeout(() => dispatch(fetchDashboard()), DASHBOARD_MIN_REFETCH_MS - elapsed);
    return () => clearTimeout(timer);
  }, [isStale, isLoading, lastFetchedAt, dispatch]);

  const handleReload = useCallback(() => dispatch(fetchDashboard()), [dispatch]);

  const handleAttentionClick = useCallback(
    (reason) => {
      dispatch(
        setAttentionFilters({
          reason,
          start_date: filters.start_date,
          end_date: filters.end_date,
        })
      );
      navigate(`/deliveries/attention?reason=${reason}`);
    },
    [dispatch, navigate, filters]
  );

  const handleFilterChange = useCallback(
    (patch) => dispatch(setFilters(patch)),
    [dispatch]
  );

  const handleStatusClick = useCallback(
    (status) => {
      dispatch(
        setDeliveryFilters({
          delivery_status: status,
          start_date: filters.start_date,
          end_date: filters.end_date,
          page: 1,
        })
      );
      navigate('/deliveries');
    },
    [dispatch, navigate, filters]
  );

  const handleActivityClick = useCallback(
    (orderId) => navigate(`/order-details/${orderId}`),
    [navigate]
  );

  const filterBar = (
    <DashboardFilters
      filters={filters}
      onFilterChange={handleFilterChange}
      onReload={handleReload}
      isLoading={isLoading}
      isStale={isStale}
    />
  );

  // Nothing to show yet: one notice takes the place of the cards, which would
  // otherwise read as a grid of convincing zeros.
  if (error && !kpis) {
    return (
      <div data-testid="dashboard-page" className="space-y-6">
        {filterBar}
        <div
          data-testid="dashboard-error"
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border"
        >
          <ErrorState title="No se pudo cargar la información" description={error} onRetry={handleReload} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {filterBar}

      {/* The numbers from before stay; the strip says they may be stale. */}
      {error && <StaleDataNotice testId="dashboard-stale-error" error={error} onRetry={handleReload} />}

      <DashboardKpis kpis={kpis} previous={previous} isLoading={isLoading} />

      <NeedsAttentionCard
        onMetricClick={handleAttentionClick}
        kpis={kpis}
        items={needsAttention}
        onItemClick={handleActivityClick}
        isLoading={isLoading}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <DashboardCharts
          daily={daily}
          range={range}
          statusCount={statusCount}
          total={kpis?.total || 0}
          onStatusClick={handleStatusClick}
          isLoading={isLoading}
        />

        <RecentActivityCard
          activity={activity}
          onEntryClick={handleActivityClick}
          isLoading={isLoading}
        />
      </div>

      <RankingCard
        testId="dashboard-ranking-communes"
        title="Dónde estás despachando"
        subtitle="Las comunas con más envíos del periodo"
        data={communeCount}
        colorFor={() => FAMILY_COLOR.delivered}
        emptyMessage="Sin comunas en el periodo seleccionado"
        isLoading={isLoading}
      />
    </div>
  );
};
